/**
 * Gestor de cambios pendientes del cuadrante
 * Maneja el estado de cambios antes de guardarlos en Firebase
 */

/**
 * Aplica un cambio a la lista de eventos procesados
 * @param {Array} processedEvents - Eventos actuales
 * @param {Object} cambio - Cambio a aplicar
 * @returns {Array} Nuevos eventos con el cambio aplicado
 */
export const aplicarCambioLocal = (processedEvents, cambio) => {
  return processedEvents.map(event => {
    if (event.id === cambio.turnoId) {
      return {
        ...event,
        vigilante: cambio.vigilante,
        vigilanteId: cambio.vigilanteId,
        sinAsignar: false,
        title: cambio.nombreVigilante || event.title,
        // Marcar como pendiente de guardar
        _pendienteGuardar: true
      };
    }
    return event;
  });
};

/**
 * Aplica múltiples cambios a la lista de eventos
 * @param {Array} processedEvents - Eventos actuales
 * @param {Array} cambios - Array de cambios
 * @returns {Array} Nuevos eventos con cambios aplicados
 */
export const aplicarCambiosLocales = (processedEvents, cambios) => {
  let eventosActualizados = [...processedEvents];
  
  cambios.forEach(cambio => {
    eventosActualizados = aplicarCambioLocal(eventosActualizados, cambio);
  });
  
  return eventosActualizados;
};

/**
 * Agrupa cambios por turno para evitar duplicados
 * @param {Array} cambios - Lista de cambios
 * @returns {Array} Cambios agrupados sin duplicados
 */
export const agruparCambios = (cambios) => {
  const cambiosPorTurno = new Map();
  
  cambios.forEach(cambio => {
    // El último cambio para cada turno prevalece
    cambiosPorTurno.set(cambio.turnoId, cambio);
  });
  
  return Array.from(cambiosPorTurno.values());
};

/**
 * Cuenta los cambios pendientes por tipo
 * @param {Array} cambios - Lista de cambios
 * @returns {Object} Estadísticas de cambios
 */
export const contarCambiosPendientes = (cambios) => {
  const cambiosAgrupados = agruparCambios(cambios);
  
  return {
    total: cambiosAgrupados.length,
    asignaciones: cambiosAgrupados.filter(c => c.tipo === 'asignar' || c.tipo === 'asignacion').length,
    eliminaciones: cambiosAgrupados.filter(c => c.tipo === 'eliminar').length
  };
};

/**
 * Valida que un cambio tenga los datos necesarios
 * @param {Object} cambio - Cambio a validar
 * @returns {Object} {valido: boolean, error: string}
 */
export const validarCambio = (cambio) => {
  if (!cambio.turnoId) {
    return { valido: false, error: 'Falta el ID del turno' };
  }
  
  if (cambio.tipo === 'asignacion') {
    // 🆕 NUEVO TIPO: Validación para asignaciones desde AsignarVigilante
    if (!cambio.vigilanteNuevo) {
      return { valido: false, error: 'Falta el objeto vigilanteNuevo' };
    }
    if (!cambio.vigilanteNuevo.persona) {
      return { valido: false, error: 'Falta el código de persona del vigilante' };
    }
    if (!cambio.vigilanteNuevo.id) {
      return { valido: false, error: 'Falta el ID del vigilante' };
    }
  }
  
  if (cambio.tipo === 'asignar') {
    if (!cambio.vigilante) {
      return { valido: false, error: 'Falta el código de persona del vigilante' };
    }
    if (!cambio.vigilanteId) {
      return { valido: false, error: 'Falta el ID del vigilante' };
    }
  }
  
  if (cambio.tipo === 'mover') {
    // Al menos debe tener fecha o vigilante para mover
    if (!cambio.fecha && cambio.vigilante === undefined) {
      return { valido: false, error: 'Debe especificar nueva fecha o vigilante' };
    }
  }
  
  return { valido: true };
};

/**
 * Crea un objeto de cambio para asignar vigilante
 * @param {string} turnoId - ID del turno
 * @param {Object} vigilanteData - Datos del vigilante
 * @returns {Object} Objeto de cambio
 */
export const crearCambioAsignacion = (turnoId, vigilanteData) => {
  return {
    tipo: 'asignar',
    turnoId,
    vigilante: vigilanteData.persona,
    vigilanteId: vigilanteData.id,
    nombreVigilante: vigilanteData.nombreCompleto || '',
    esVigilanteExterno: vigilanteData.esExterno || false,
    servicioPrincipal: vigilanteData.esExterno ? vigilanteData.servicioPrincipal || null : null,
    fechaCambio: new Date().toISOString()
  };
};

/**
 * Crea un objeto de cambio para eliminar asignación
 * @param {string} turnoId - ID del turno
 * @returns {Object} Objeto de cambio
 */
export const crearCambioEliminacion = (turnoId) => {
  return {
    tipo: 'eliminar',
    turnoId,
    fechaCambio: new Date().toISOString()
  };
};

/**
 * Crea un objeto de cambio para mover un turno (cambiar fecha o vigilante)
 * @param {string} turnoId - ID del turno
 * @param {Object} cambios - Cambios a aplicar {fecha?, vigilante?, vigilanteId?}
 * @returns {Object} Objeto de cambio
 */
export const crearCambioMovimiento = (turnoId, cambios) => {
  return {
    tipo: 'mover',
    turnoId,
    ...cambios,
    fechaCambio: new Date().toISOString()
  };
};

/**
 * Aplica un cambio de movimiento a la lista de eventos
 * @param {Array} processedEvents - Eventos actuales
 * @param {Object} cambio - Cambio de movimiento a aplicar
 * @returns {Array} Nuevos eventos con el cambio aplicado
 */
export const aplicarCambioMovimiento = (processedEvents, cambio) => {
  return processedEvents.map(event => {
    if (event.id === cambio.turnoId) {
      const eventoActualizado = { ...event };
      
      // Actualizar fecha si se proporciona
      if (cambio.fecha) {
        eventoActualizado.fecha = cambio.fecha;
        // Actualizar también las fechas de start y end
        const [year, month, day] = cambio.fecha.split('-');
        const newStart = new Date(event.start);
        newStart.setFullYear(parseInt(year), parseInt(month) - 1, parseInt(day));
        eventoActualizado.start = newStart;
        
        const newEnd = new Date(event.end);
        newEnd.setFullYear(parseInt(year), parseInt(month) - 1, parseInt(day));
        eventoActualizado.end = newEnd;
      }
      
      // Actualizar vigilante si se proporciona
      if (cambio.vigilante !== undefined) {
        eventoActualizado.vigilante = cambio.vigilante;
        eventoActualizado.vigilanteId = cambio.vigilanteId;
        eventoActualizado.title = cambio.nombreVigilante || cambio.vigilante;
        eventoActualizado.sinAsignar = !cambio.vigilante;
      }
      
      // Marcar como pendiente de guardar
      eventoActualizado._pendienteGuardar = true;
      
      return eventoActualizado;
    }
    return event;
  });
};

